import { useState } from 'react'
import { MessageSquare, Search } from 'lucide-react'

import { cn } from 'components/UI/cn'
import {
  useGetCampaignReplies,
  useGetCampaignSequence,
} from 'features/campaigns/sequences_api'

interface Props {
  campaignId: number
}

export function CampaignDashboardRepliesTab({ campaignId }: Props) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedReplyId, setSelectedReplyId] = useState<number | null>(null)

  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId)
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0

  const { data: repliesData, isLoading } =
    useGetCampaignReplies(bisonCampaignId)

  const replies = repliesData?.results ?? []

  const filteredReplies = replies.filter((reply) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      reply.from_name?.toLowerCase().includes(q) ||
      reply.from_email_address?.toLowerCase().includes(q) ||
      reply.text_body?.toLowerCase().includes(q) ||
      reply.lead_full_name?.toLowerCase().includes(q)
    )
  })

  const selectedReply = replies.find((r) => r.id === selectedReplyId)

  if (isLoadingSequence || (bisonCampaignId > 0 && isLoading)) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="size-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    )
  }

  if (replies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-3 rounded-full bg-surface-muted p-4">
          <MessageSquare className="size-8 text-fg-muted" />
        </div>
        <p className="text-base text-fg">No replies yet</p>
        <p className="mt-1 text-sm text-fg-secondary">
          Replies from your campaign contacts will appear here.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-muted" />
        <input
          type="text"
          placeholder="Search replies..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-lg border border-line bg-surface py-2 pl-9 pr-4 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
      </div>

      <div
        className="flex gap-0 overflow-hidden rounded-lg border border-line bg-surface"
        style={{ minHeight: 480 }}
      >
        <div className="w-80 shrink-0 border-r border-line">
          {filteredReplies.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-fg-muted">
              No replies match your search
            </div>
          ) : (
            filteredReplies.map((reply) => {
              return (
                <button
                  key={reply.id}
                  onClick={() => setSelectedReplyId(reply.id)}
                  className={cn(
                    'w-full border-b border-line px-4 py-3.5 text-left transition-colors last:border-b-0',
                    selectedReplyId === reply.id
                      ? 'bg-surface-hover'
                      : 'hover:bg-surface-hover'
                  )}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-medium text-fg">
                      {reply.from_name ||
                        reply.lead_full_name ||
                        reply.from_email_address}
                    </span>
                    <span className="text-xs text-fg-muted">
                      {reply.date_received
                        ? new Date(reply.date_received).toLocaleDateString()
                        : ''}
                    </span>
                  </div>
                  <p className="mb-2 truncate text-xs text-fg-secondary">
                    {reply.text_body}
                  </p>
                  {reply.is_interested && (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                      Interested
                    </span>
                  )}
                </button>
              )
            })
          )}
        </div>

        <div className="flex flex-1 flex-col">
          {selectedReply ? (
            <div className="p-5">
              <div className="mb-4">
                <p className="text-sm font-medium text-fg">
                  {selectedReply.from_name ||
                    selectedReply.lead_full_name ||
                    selectedReply.from_email_address}
                </p>
                <p className="text-xs text-fg-muted">
                  {selectedReply.from_email_address}
                </p>
              </div>
              <div className="rounded-lg border border-line bg-surface-muted p-4">
                <p className="whitespace-pre-wrap text-sm text-fg">
                  {selectedReply.text_body}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-fg-muted">
              Select a reply to view details
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
